import React, { useState, useEffect } from "react";
import _ from "lodash";
import { Dialog, DialogTitle, DialogContent, Paper, IconButton, Typography, Grid, CircularProgress, MuiThemeProvider } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import mimsLogo from "../../assets/images/MIMS-logo-big.png";

import api from "../../store/api";

const useStyles = makeStyles({
  paper: {
    margin: 0,
    position: "absolute",
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    borderRadius: 0,
    maxHeight: "100%",
  },
  headerTitle: {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: 400,
  },
  title: {
    fontSize: "60px",
    fontWeight: 900,
    color: "#333",
    margin: "0 0 0 70px",
  },
  closeIcon: {
    position: "absolute",
    top: "8px",
    right: "8px",
  },
  progress: {
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0,
    flex: "1 0",
    width: "100%",
  },
  contentContainer: {
    backgroundColor: "#fff",
    display: "flex",
    height: "100%",
    flexDirection: "row",
    justifyContent: "center",
    paddingTop: "56px",
  },
  loader: {
    alignSelf: "center",
  },
  logo: {
    position: "absolute",
    bottom: "76px",
    left: "28px",
  },
  logoImage: {
    width: "220px",
    height: "80px"
  },
  leftSide: {
    position: "relative",
  },
  prodDetails: {
    color: "#333333",
    fontSize: "18px",
    paddingBottom: "54px",

    "& h2": {
      color: "#02489d",
      fontSize: "34px",
      margin: "40px 0 30px 0",

      "&:first-of-type": {
        marginTop: "16px",
      },
    },

    "& h3": {
      color: "#333333",
      fontSize: "24px",
      marginTop: "22px",
    },
  },
});

const createMarkup = (str: string) => {
  if (!str || str == "") return { __html: "None" };

  type rmType = {
    [key: string]: string;
  };
  const replaceMapping: rmType = {
    "<br>": "<p>",
    "<hd2>": "<h4>",
    "</hd2>": "</h4>",
    "<hd2i>": "<h4><i>",
    "</hd2i>": "</i></h4>",
    "<hd4>": "<h5>",
    "</hd4>": "</h5>",
    "<hd4i>": "<h5><i>",
    "</hd4i>": "</i></h5>",
  };
  var re = new RegExp(Object.keys(replaceMapping).join("|"), "gi");
  const s = str.replace(re, (matched: string): string => {
    return replaceMapping[matched];
  });
  const s2 = s.replace(/(https?:\/\/[^ ]*\.(?:png|jpg|gif|jpeg))/gi, (matched: string): string => `<img src="${matched}" /><br />`);
  return { __html: s2 };
};

const ProductDetails = ({ pd }: any) => {
  const classes = useStyles();

  return (
    <div className={classes.prodDetails}>
      {pd.map((block: any, index: number) => {
        return (
          <div key={index}>
            <h2>{block.header}</h2>
            <p dangerouslySetInnerHTML={createMarkup(block.text)} />
            {block.subContent &&
              block.subContent.map((sc: any, subIndex: number) => {
                return (
                  <div key={`${index}_${subIndex}`}>
                    <h3>{sc.header}</h3>
                    {sc.text && <p dangerouslySetInnerHTML={createMarkup(sc.text)} />}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};

const MedicationDetails = ({ onClose, medicationInfo, medicationName }: any) => {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [productInfo, setProductInfo] = useState<any>();
  const classes = useStyles();

  const loadProductInfoDetails = async (id: string, type: string) => {
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await api.getBrandMonographDetails(`monographId=${id}&monographType=${type}`);
      setProductInfo(res.data?.content);
    } catch {
      setIsError(true);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadProductInfoDetails(medicationInfo.monographId, medicationInfo.monographType);
  }, [medicationInfo]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth={true}
      maxWidth="xl"
      keepMounted={true}
      classes={{
        paper: classes.paper,
      }}
    >
      <DialogTitle>
        <Typography className={classes.headerTitle}>{`Medication Information - ${medicationName}`}</Typography>
        <IconButton aria-label="close" onClick={onClose} className={classes.closeIcon}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.contentContainer}>
        {isLoading && !isError && <CircularProgress className={classes.loader} />}

        {isError && (
          <Alert variant="outlined" severity="error">
            There was a network error. Please try again.
          </Alert>
        )}

        {!isLoading && !isError && (
          <Grid container spacing={2}>
            <Grid item xs={5} className={classes.leftSide}>
              <h1 className={classes.title}>{medicationName}</h1>
              <div className={classes.logo}>
                <img src={mimsLogo} className={classes.logoImage} />
              </div>
            </Grid>
            <Grid item xs={7}>
              <ProductDetails pd={productInfo} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MedicationDetails;
